import { Card, Stack, Group, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { Link } from 'react-router-dom';
import { useT } from '../../../i18n/I18nProvider';
import type { Doc } from '../spec';
import { CodeBlock } from './CodeBlock';

/**
 * The first five minutes: make a key, call something, read what came back.
 *
 * The concrete values (the base URL, the scope table, the version) are read
 * from the document rather than written here, so this section cannot describe a
 * scope vocabulary the platform no longer has.
 */
export function GettingStarted({
  doc,
  baseUrl,
  apiKey
}: {
  doc: Doc | undefined;
  baseUrl: string;
  apiKey: string;
}) {
  const t = useT();
  const key = apiKey.trim() || 'rk_live_your_key_here';
  const scopes = doc?.['x-rr-scopes'] ?? [];

  // The three blocks below are a curl command and two JSON bodies, shown
  // exactly as a developer would type and receive them. They are assembled line
  // by line rather than written as one template literal so that the line
  // carrying a wire value can also carry its own `i18n-ignore`: a comment
  // inside a template literal would end up printed on the page as part of the
  // JSON. Translating a wire format would make it wrong, which is the point.
  const firstCall = [
    `curl "${baseUrl}/v1/contacts?limit=1" \\`,
    `  -H "X-Api-Key: ${key}"` // i18n-ignore: an HTTP header, not copy
  ].join('\n');

  const success = [
    '{',
    '  "data": [',
    '    {',
    '      "id": "01a06f2e-2c81-7bd1-9a44-6f8c9d2e5a13",', // i18n-ignore: a JSON response body
    '      "msisdn": "+27821234567",', // i18n-ignore: a JSON response body
    '      "name": "Thandi Mokoena",', // i18n-ignore: a JSON response body
    '      "tags": ["vip"]', // i18n-ignore: a JSON response body
    '    }',
    '  ],',
    '  "meta": { "total": 1 }', // i18n-ignore: a JSON response body
    '}'
  ].join('\n');

  const failure = [
    '{',
    '  "error": {',
    '    "code": "INSUFFICIENT_SCOPE",', // i18n-ignore: a JSON error body
    '    "message": "This API key does not hold the \'contacts:read\' scope, which GET /v1/contacts requires.",', // i18n-ignore: a JSON error body
    '    "details": {',
    '      "required_scope": "contacts:read",', // i18n-ignore: a JSON error body
    '      "granted_scopes": ["messages:send"]', // i18n-ignore: a JSON error body
    '    },',
    '    "request_id": "0HNODM6L1E8C9:00000001"', // i18n-ignore: a JSON error body
    '  }',
    '}'
  ].join('\n');

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.start.step1.title')}</Title>
          <Text role="body">{t('developers.start.step1.body')}</Text>
          <Group gap="sm" wrap="wrap">
            <Link to="/settings/api-keys">{t('developers.start.step1.link')}</Link>
          </Group>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('developers.start.step1.once')}
          </Text>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.start.step2.title')}</Title>
          <Text role="body">{t('developers.start.step2.body')}</Text>
          <CodeBlock code={baseUrl} label={t('developers.start.baseUrl')} />
          <CodeBlock code={`X-Api-Key: ${key}`} label={t('developers.start.header')} />
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('developers.start.step2.serverSide')}
          </Text>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.start.step3.title')}</Title>
          <Text role="body">{t('developers.start.step3.body')}</Text>
          <CodeBlock code={firstCall} label={t('developers.start.firstCall')} />
          <Stack gap="xs">
            <Text role="label">{t('developers.start.success')}</Text>
            <CodeBlock code={success} label={t('developers.start.success')} />
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('developers.start.successNote')}
            </Text>
          </Stack>
          <Stack gap="xs">
            <Text role="label">{t('developers.start.failure')}</Text>
            <CodeBlock code={failure} label={t('developers.start.failure')} />
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('developers.start.failureNote')}
            </Text>
          </Stack>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.start.scopes.title')}</Title>
          <Text role="body">{t('developers.start.scopes.body')}</Text>
          <Stack gap="sm">
            {scopes.map((scope) => (
              <Group key={scope.name} gap="sm" align="flex-start" wrap="nowrap">
                <Badge variant="light" className="rr-dev__nowrap-badge rr-dev__identifier">
                  {scope.name}
                </Badge>
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {scope.description}
                </Text>
              </Group>
            ))}
          </Stack>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('developers.start.scopes.fixed')}
          </Text>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.start.limits.title')}</Title>
          <Text role="body">{t('developers.start.limits.body')}</Text>
          <Text role="body">{t('developers.start.pagination')}</Text>
          <Text role="body">{t('developers.start.errors')}</Text>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('developers.start.promise.title')}</Title>
          <Text role="body">{t('developers.start.promise.stable')}</Text>
          <Text role="body">{t('developers.start.promise.additive')}</Text>
          <Text role="body">{t('developers.start.promise.removal')}</Text>
          <Text role="body">{t('developers.start.promise.breaking')}</Text>
        </Stack>
      </Card>
    </Stack>
  );
}
